import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Coloso de escarcha (era IV, docs/CRIATURAS.md «Bastión de la Vigilia»): the cold of the titan's back dreamt
 * into a giant. As big as the sleeping giant or more: a hunched colossus of old blue ice over black glacier
 * rock, shoulders like a pass in winter, arms that hang to its knees and end in boulder fists, a small head sunk
 * forward between the shoulders under a crown of icicles. Translucent blue crystal grows out of its shoulders
 * and down its spine in great shards; frost crusts every ledge; a cold light burns inside it (the seams of its
 * chest, its eyes, its maw) and a freezing mist hangs round its feet and smokes off its shoulders.
 *
 * Bones: body (hips), chest, head, jaw, `breath` (a cone of frost at the mouth, built tiny: the breath clip
 * scales it by `1 / FROST_BREATH_REST` to show it at full length), arms in three (arm, fore, hand), legs (leg, shin).
 * No rest rotations: the hunch is modelled. Faces +Z with its `R` limbs on +X. Opaque shared materials, the
 * crystal its one translucent lit material, the frost mist and breath unlit, plus glow sprites (no lights).
 */

/** How small the breath cone is built (the clip scales `breath` by about 1 / FROST_BREATH_REST at full blast). */
export const FROST_BREATH_REST = 0.02;
export const FROST_COLOSSUS_HEIGHT = 2.75;

export const frostColossus = (o?: BaseOpts): Model => {
  const c = begin(o, 1.1, 811);
  const { g, rng } = c;
  const k4 = kit(rng);

  // Black glacier rock at the core, old blue ice over it, white frost crust on top, the crystal, the cold light.
  const rock = std('#141b24', { roughness: 0.8, flatShading: true, bumpScale: 0.05 });
  const ice = std('#2f5371', { roughness: 0.3, metalness: 0.1, flatShading: true, bumpScale: 0.02 });
  const iceL = std('#5f8dad', { roughness: 0.25, metalness: 0.1, flatShading: true, bumpScale: 0.02 });
  const frost = std('#d6e8f4', { roughness: 0.9, flatShading: true, bumpScale: 0.06 });
  const crystal = emis('#a9e2ff', '#2f8fd8', 0.75, { transparent: true, opacity: 0.82, roughness: 0.15, flatShading: true });
  const cold = emis('#f2fbff', '#7fd8ff', 2.6);
  const mistM = basic('#cfe8fa', 0.32);

  /** A faceted column of ice from `a` to `b` (a limb), turned at random so its facets catch the light. */
  const col = (parent: THREE.Object3D, mat: THREE.Material, a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number): void => {
    const m = limb(parent, a, b, r1, r2, mat, 6);
    m.rotateY(rng() * 6.3);
  };
  /** An angular plate of ice (a flat-ish shard of a lump) on a limb's outside. */
  const plate = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number): void => {
    k4.lump(parent, mat, x, y, z, sx, sy, sz, 0.6, 0, 0.12);
  };

  /** A cluster of crystal shards from `at`, fanned round `dir`. */
  const cluster = (parent: THREE.Object3D, at: THREE.Vector3, dir: THREE.Vector3, n: number, len: number, r: number): void => {
    for (let i = 0; i < n; i++) {
      const d = dir.clone().add(V((rng() - 0.5) * 0.9, (rng() - 0.5) * 0.5, (rng() - 0.5) * 0.9));
      const l = len * (0.55 + rng() * 0.6) * (i === 0 ? 1.25 : 1);
      k4.shard(parent, crystal, at.clone().add(V((rng() - 0.5) * 0.12, (rng() - 0.5) * 0.06, (rng() - 0.5) * 0.12)), d, l, r * (0.7 + rng() * 0.5));
    }
  };

  /* ---------- legs: short pillars of ice on boulder feet ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.32, 0.95, 0), `leg${s}`);
    const hip = legB.abs;
    col(hip, ice, V(k * 0.33, 0.98, 0), V(k * 0.36, 0.5, 0.03), 0.25, 0.21);
    plate(hip, iceL, k * 0.42, 0.78, 0.1, 0.16, 0.22, 0.12);
    k4.lump(hip, frost, k * 0.36, 0.98, 0.06, 0.2, 0.07, 0.18, 0.3, 0);
    const shinB = joint(hip, V(k * 0.35, 0.5, 0.02), `shin${s}`);
    const lo = shinB.abs;
    col(lo, rock, V(k * 0.36, 0.52, 0.03), V(k * 0.36, 0.14, 0.06), 0.2, 0.22);
    plate(lo, ice, k * 0.38, 0.4, 0.17, 0.17, 0.2, 0.1);
    k4.lump(lo, ice, k * 0.36, 0.1, 0.1, 0.25, 0.11, 0.31, 0.2, 1);
    for (let i = -1; i <= 1; i++) k4.shard(lo, iceL, V(k * 0.36 + i * 0.1, 0.06, 0.32), V(i * 0.2, -0.1, 1), 0.14, 0.04);
    k4.shard(lo, crystal, V(k * 0.46, 0.38, 0.0), V(k, 0.6, -0.2), 0.28, 0.05);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips and belly ---------- */
  const bodyB = joint(g, V(0, 1.0, 0), 'body');
  const body = bodyB.abs;
  k4.lump(body, rock, 0, 1.02, 0, 0.46, 0.26, 0.34, 0.2, 1);
  k4.lump(body, ice, 0, 1.12, 0.12, 0.38, 0.2, 0.28, 0.2, 1);

  /* ---------- chest: a hunched wall of ice, crystal shards down the spine, the cold heart in its seams ---------- */
  const chestB = joint(body, V(0, 1.2, 0), 'chest');
  const chest = chestB.abs;
  k4.lump(chest, ice, 0, 1.38, 0.12, 0.48, 0.32, 0.38, 0.2, 1, 0.12);
  k4.lump(chest, ice, 0, 1.78, 0.06, 0.72, 0.44, 0.48, 0.2, 1, 0.12);
  k4.lump(chest, rock, 0, 1.86, -0.16, 0.62, 0.44, 0.42, 0.2, 1, 0.12);
  for (const k of [-1, 1]) plate(chest, iceL, k * 0.27, 1.72, 0.44, 0.26, 0.22, 0.13);
  for (const k of [-1, 1]) plate(chest, iceL, k * 0.2, 1.36, 0.42, 0.17, 0.14, 0.1);
  // Frost crusting the top of the back and the shoulders.
  k4.lump(chest, frost, 0, 2.18, -0.1, 0.44, 0.12, 0.3, 0.3, 1);
  // The seams of the cold heart, out from the breastbone, and its glow.
  const heart = V(0, 1.62, 0.52);
  for (let i = 0; i < 4; i++) {
    const a = -0.9 + i * 1.25;
    const pts = [heart.clone()];
    for (let j = 1; j < 4; j++) pts.push(V(Math.cos(a) * j * 0.12, 1.62 + Math.sin(a) * j * 0.1, 0.52 - j * j * 0.012));
    k4.crack(chest, pts, 0.012, cold, 0.04);
  }
  glowSprite(chest, '#9fdcff', 0.75, V(0, 1.62, 0.58), 0.55);
  // The spine: great shards of crystal raking up and back.
  for (let i = 0; i < 5; i++) {
    const y = 2.22 - i * 0.16, z = -0.28 - i * 0.07;
    const x = (i % 2 ? 1 : -1) * 0.12;
    k4.shard(chest, crystal, V(x, y, z), V(x * 1.5, 1, -0.9 - i * 0.15), 0.75 - i * 0.08, 0.1);
    k4.shard(chest, crystal, V(-x * 0.6, y - 0.05, z - 0.02), V(-x * 3, 0.8, -1), 0.45 - i * 0.05, 0.07);
  }

  /* ---------- head: sunk forward between the shoulders, a crown of icicles, eyes and maw of cold light ---------- */
  const headB = joint(chest, V(0, 1.98, 0.32), 'head');
  const head = headB.abs;
  k4.lump(head, ice, 0, 2.0, 0.5, 0.21, 0.19, 0.22, 0.3, 1);
  // The brow: one heavy shelf of frost over the eyes.
  k4.lump(head, frost, 0, 2.1, 0.6, 0.2, 0.06, 0.12, 0.2, 0);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.03, 6, 4), cold, head).position.set(k * 0.085, 2.03, 0.69);
    glowSprite(head, '#bfeaff', 0.26, V(k * 0.085, 2.03, 0.72), 0.95);
  }
  // A crown of icicles raking back.
  for (let i = 0; i < 5; i++) {
    const a = (i - 2) * 0.4;
    k4.shard(head, crystal, V(Math.sin(a) * 0.16, 2.14, 0.48 - Math.abs(a) * 0.06), V(Math.sin(a) * 0.8, 1, -0.5), 0.32 - Math.abs(i - 2) * 0.05, 0.045);
  }
  const jawB = joint(head, V(0, 1.92, 0.48), 'jaw');
  const jaw = jawB.abs;
  k4.lump(jaw, ice, 0, 1.84, 0.6, 0.18, 0.08, 0.14, 0.2, 0);
  for (const k of [-1, 1]) {
    for (let i = 0; i < 2; i++) {
      const t = M(new THREE.ConeGeometry(0.022, 0.08, 4), frost, jaw);
      t.position.set(k * (0.05 + i * 0.06), 1.92, 0.68 - i * 0.03);
    }
    const f = M(new THREE.ConeGeometry(0.02, 0.07, 4), frost, head);
    f.position.set(k * 0.07, 1.9, 0.7);
    f.rotation.x = Math.PI;
  }
  // The cold in its throat.
  M(new THREE.SphereGeometry(0.06, 6, 4), cold, head).position.set(0, 1.9, 0.62);
  glowSprite(jaw, '#a9e4ff', 0.45, V(0, 1.89, 0.72), 0.6);
  // The breath: a cone of frost from the mouth, forward and down (built tiny; see FROST_BREATH_REST).
  const breathB = joint(head, V(0, 1.89, 0.7), 'breath');
  {
    const L = 2.6 * FROST_BREATH_REST, R = 0.85 * FROST_BREATH_REST;
    const bg = G(breathB.abs, 0, 1.89, 0.7);
    bg.rotation.x = Math.PI / 2 + 0.38;
    const outer = new THREE.ConeGeometry(R, L, 9, 1, true);
    outer.translate(0, -L / 2, 0);
    outer.rotateX(Math.PI);
    M(outer, mistM, bg);
    const inner = new THREE.ConeGeometry(R * 0.45, L * 0.8, 7, 1, true);
    inner.translate(0, -L * 0.4, 0);
    inner.rotateX(Math.PI);
    M(inner, crystal, bg);
  }

  /* ---------- arms: huge, hanging to the knees, boulder fists; crystal on the shoulders and forearms ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(chest, V(k * 0.72, 1.92, 0), `arm${s}`);
    const sh = armB.abs;
    k4.lump(sh, ice, k * 0.76, 1.96, 0, 0.34, 0.3, 0.34, 0.25, 1, 0.12);
    k4.lump(sh, frost, k * 0.78, 2.2, -0.02, 0.27, 0.08, 0.25, 0.3, 0);
    cluster(sh, V(k * 0.84, 2.14, -0.06), V(k * 0.6, 1, -0.3), 6, 0.7, 0.09);
    col(sh, ice, V(k * 0.8, 1.85, 0), V(k * 0.88, 1.28, 0.06), 0.2, 0.16);
    plate(sh, iceL, k * 0.95, 1.6, 0.0, 0.12, 0.24, 0.14);
    const foreB = joint(sh, V(k * 0.88, 1.28, 0.06), `fore${s}`);
    const fo = foreB.abs;
    k4.lump(fo, rock, k * 0.89, 1.27, 0.06, 0.17, 0.15, 0.17, 0.3, 0);
    col(fo, ice, V(k * 0.89, 1.27, 0.06), V(k * 0.92, 0.66, 0.16), 0.17, 0.25);
    plate(fo, iceL, k * 1.03, 0.95, 0.1, 0.13, 0.28, 0.18);
    plate(fo, frost, k * 0.95, 1.12, 0.2, 0.12, 0.05, 0.1);
    k4.shard(fo, crystal, V(k * 1.08, 1.05, 0.04), V(k, 0.4, -0.5), 0.36, 0.06);
    k4.shard(fo, crystal, V(k * 1.05, 0.85, 0.12), V(k, 0.2, -0.2), 0.26, 0.05);
    const handB = joint(fo, V(k * 0.92, 0.66, 0.16), `hand${s}`);
    const ha = handB.abs;
    k4.lump(ha, ice, k * 0.92, 0.48, 0.2, 0.28, 0.24, 0.29, 0.3, 1, 0.14);
    k4.lump(ha, frost, k * 0.93, 0.62, 0.2, 0.16, 0.06, 0.16, 0.3, 0);
    // Knuckles of black rock.
    for (let i = -1; i <= 1; i++) k4.lump(ha, rock, k * 0.92 + i * 0.11, 0.36, 0.38, 0.08, 0.08, 0.08, 0.5, 0);
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
    arms[`hand${s}`] = handB.p;
  }

  /* ---------- the freezing mist: glows hanging round its feet, vapour smoking off its shoulders ---------- */
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    glowSprite(g, '#bfe3ff', 1.1 + rng() * 0.4, V(Math.cos(a) * 0.55, 0.18, Math.sin(a) * 0.5), 0.16);
  }
  glowSprite(g, '#9fd2ff', 2.6, V(0, 1.2, 0), 0.07);
  for (const k of [-1, 1]) glowSprite(chest, '#cfeaff', 0.9, V(k * 0.85, 2.25, -0.1), 0.18);

  const t = (): void => undefined;
  return done(c, FROST_COLOSSUS_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, jaw: jawB.p, breath: breathB.p,
    armL: arms.armL!, foreL: arms.foreL!, handL: arms.handL!, armR: arms.armR!, foreR: arms.foreR!, handR: arms.handR!,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
